import type { RouteStep, RouteTrack } from "@/app/queries";
import { Button } from "@/ui/Button";
import { Eyebrow, StateChip, StepActionLink } from "./bits";
import { lockReason, nextAfter, stepDescription } from "./chain";

/* Панель «выбранный шаг» (S2): чип состояния, крошка, состав, кнопка по
   состоянию и «что откроет гейт». Только из данных маршрута — документ
   закрытого шага сервер не отдаёт, поэтому счётчиков здесь нет. */

function rows(step: RouteStep): { k: string; v: string }[] {
  const s = step.state;
  const locked = s === "locked";
  const done = s === "done";
  return [
    { k: "ресурсы", v: locked ? "откроются позже" : done ? "пройдены" : "открыть" },
    { k: "практика", v: locked ? "откроется позже" : done ? "сдана" : "открыть" },
    { k: "гейт", v: done ? "сдан" : locked ? "закрыт" : "«Готов к переходу»" },
    { k: "банк вопросов", v: locked ? "закрыт" : "открыт" },
  ];
}

export function MapAside({ track, step }: { track: RouteTrack; step: RouteStep }) {
  const crumb = track.parallel ? `${track.name} (параллельный трек) · шаг ${step.position} из ${track.total}` : `${track.name} · шаг ${step.position} из ${track.total}`;
  const unlocks = track.parallel ? [nextAfter(track, step), "Секция алгоритмов на мок-собеседовании"] : [nextAfter(track, step), "Банк вопросов по теме шага"];
  return (
    <aside data-testid="map-aside" className="flex flex-col gap-4 px-5 py-[22px] md:border-l md:border-bd">
      <Eyebrow>выбранный шаг</Eyebrow>
      <section className="rounded-md border border-bd bg-card p-4">
        <div className="flex flex-wrap items-center gap-2.5">
          <StateChip state={step.state} />
          <span className="font-mono text-[11px] text-mut">{crumb}</span>
        </div>
        <h2 className="mb-2 mt-3 font-mono text-[18px] font-bold leading-[1.25] tracking-[-.01em] text-ink">{step.title}</h2>
        <p className="m-0 text-[12.5px] leading-[1.5] text-ink2">{stepDescription(track, step)}</p>
        <dl className="my-4 grid grid-cols-[auto_1fr] gap-x-4 gap-y-1.5 font-mono text-[11.5px]">
          {rows(step).map((r) => (
            <div key={r.k} className="contents">
              <dt className="text-mut">{r.k}</dt>
              <dd className="m-0 text-right text-ink2">{r.v}</dd>
            </div>
          ))}
        </dl>
        {step.state === "locked" ? (
          <div className="flex flex-wrap">
            <Button disabled reason={lockReason(step)}>
              сдай предыдущий гейт
            </Button>
          </div>
        ) : (
          <StepActionLink id={step.id} state={step.state} full />
        )}
      </section>
      <section className="rounded-md border border-bd bg-card p-4">
        <Eyebrow>что откроет гейт</Eyebrow>
        <ul className="m-0 mt-3 flex list-none flex-col gap-2 p-0 text-[12.5px] text-ink">
          {unlocks.map((u) => (
            <li key={u} className="flex gap-2">
              <span aria-hidden className="font-mono text-acc">
                →
              </span>
              <span>{u}</span>
            </li>
          ))}
        </ul>
      </section>
    </aside>
  );
}
